import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, doc, updateDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../hooks/useUserData";
import { Wrench, Clock, CheckCircle, AlertTriangle, Filter, Search } from "lucide-react";
import { addLog } from "../utils/audit";

export default function TechnicianTasks() {
  const navigate = useNavigate();
  const [user, userLoading] = useUserData();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (userLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    // Listen to maintenance tasks assigned to this technician
    const tasksQuery = query(
      collection(db, "maintenanceTasks"),
      where("assignedTechnician", "==", user.uid)
    );

    const unsubTasks = onSnapshot(
      tasksQuery,
      (snap) => {
        console.log("TechnicianTasks: Received snapshot with", snap.docs.length, "documents");
        const tasksData = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        console.log("TechnicianTasks: Raw tasks data:", tasksData);

        // Sort tasks: high priority first, then medium, then low
        const priorityOrder = { high: 1, medium: 2, low: 3 };
        tasksData.sort((a, b) => {
          const aPriority = priorityOrder[a.priority?.toLowerCase()] || 4;
          const bPriority = priorityOrder[b.priority?.toLowerCase()] || 4;
          return aPriority - bPriority;
        });

        console.log("TechnicianTasks: After sorting:", tasksData.length, "tasks");
        setTasks(tasksData);
        setFilteredTasks(tasksData);
        setLoading(false);
      },
      (error) => {
        console.error("Error listening to maintenance tasks:", error);
        setLoading(false);
      }
    );

    return () => unsubTasks();
  }, [user, userLoading, navigate]);

  useEffect(() => {
    let filtered = tasks;

    // Apply status filter
    if (filter !== "all") {
      filtered = filtered.filter(task => task.status === filter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTasks(filtered);
  }, [tasks, filter, searchTerm]);

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      await updateDoc(doc(db, "maintenanceTasks", taskId), {
        status: newStatus,
        lastUpdated: new Date(),
        updatedBy: user.uid
      });

      // Log the action
      await addLog(user.uid, "MAINTENANCE_STATUS_UPDATE", {
        taskId,
        oldStatus: tasks.find(t => t.id === taskId)?.status,
        newStatus,
        assetName: tasks.find(t => t.id === taskId)?.assetName
      });

    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending": return "text-warning-600 bg-warning-50";
      case "in-progress": return "text-info-600 bg-info-50";
      case "completed": return "text-success-600 bg-success-50";
      case "awaiting-parts": return "text-neutral-600 bg-neutral-50";
      case "cancelled": return "text-error-600 bg-error-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "text-error-600 bg-error-50";
      case "medium": return "text-warning-600 bg-warning-50";
      case "low": return "text-info-600 bg-info-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <div className="skeleton-avatar mb-4"></div>
        <div className="skeleton-title w-48 mb-2"></div>
        <div className="skeleton-text w-32"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 animate-fade-in">
      <div className="w-full px-4 pt-20 space-y-6 lg:px-8 xl:px-12">
        {/* Search and Filter */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex flex-wrap gap-2 sm:overflow-x-auto sm:flex-nowrap">
            {["all", "pending", "in-progress", "completed", "awaiting-parts"].map((statusFilter) => (
              <button
                key={statusFilter}
                onClick={() => setFilter(statusFilter)}
                className={`px-3 py-2 sm:px-4 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                  filter === statusFilter
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                }`}
              >
                {statusFilter === "all" ? "All" :
                 statusFilter === "in-progress" ? "In Progress" :
                 statusFilter === "awaiting-parts" ? "Need Parts" :
                 statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-4">
          {filteredTasks.length === 0 ? (
            <div className="card text-center py-12">
              <Wrench className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                {tasks.length === 0 ? "No Tasks Assigned" : "No Tasks Match Filter"}
              </h3>
              <p className="text-neutral-600">
                {tasks.length === 0
                  ? "You don't have any maintenance tasks assigned yet."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStatusUpdate={handleStatusUpdate}
                onClick={() => navigate(`/technician/task/${task.id}`)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Task Card Component
function TaskCard({ task, onStatusUpdate, onClick }) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending": return "text-warning-600 bg-warning-50";
      case "in-progress": return "text-info-600 bg-info-50";
      case "completed": return "text-success-600 bg-success-50";
      case "awaiting-parts": return "text-neutral-600 bg-neutral-50";
      case "cancelled": return "text-error-600 bg-error-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "text-error-600 bg-error-50";
      case "medium": return "text-warning-600 bg-warning-50";
      case "low": return "text-info-600 bg-info-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const canUpdateStatus = (currentStatus, newStatus) => {
    const validTransitions = {
      "pending": ["in-progress", "cancelled"],
      "in-progress": ["completed", "awaiting-parts", "pending"],
      "awaiting-parts": ["in-progress", "cancelled"],
      "completed": [],
      "cancelled": []
    };
    return validTransitions[currentStatus]?.includes(newStatus) || false;
  };

  return (
    <div onClick={onClick} className="card-interactive p-4 cursor-pointer w-full max-w-full overflow-hidden">
      {/* Mobile-first vertical stack - optimized for full width */}
      <div className="space-y-3 w-full">
        {/* Header row with asset name and priority */}
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-start justify-between gap-2 w-full">
            <h3 className="font-semibold text-neutral-900 text-base flex-1 leading-tight min-w-0 truncate" title={task.assetName || "Unnamed Asset"}>
              {task.assetName || "Unnamed Asset"}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)} flex-shrink-0`}>
              {task.priority || "Low"}
            </span>
          </div>
          <p className="text-sm text-neutral-600 leading-relaxed w-full">{task.description}</p>
        </div>

        {/* Status badge - full width centered */}
        <div className="flex items-center justify-center w-full">
          <span className={`px-3 py-2 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
            {task.status || "Pending"}
          </span>
        </div>

        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500 w-full">
          <div className="flex-1 min-w-0">
            {task.location && <span className="flex items-center gap-1 truncate">📍 <span className="truncate">{task.location}</span></span>}
          </div>
          <div className="flex-1">
            {task.requesterName && <span className="flex items-center gap-1 truncate">👤 <span className="truncate">{task.requesterName}</span></span>}
          </div>
          <div className="flex-shrink-0">
            {task.dueDate && <span className="flex items-center gap-1">📅 {new Date(task.dueDate.toDate()).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Action buttons - separated section, prevent overflow */}
        <div className="pt-3 border-t border-gray-100 w-full">
          <div className="flex flex-wrap justify-center gap-2 w-full">
            {["pending", "in-progress", "completed", "awaiting-parts"].map((status) => {
              if (!canUpdateStatus(task.status, status)) return null;
              return (
                <button
                  key={status}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusUpdate(task.id, status);
                  }}
                  className={`px-3 py-2 rounded-full text-xs sm:text-sm font-medium flex-shrink-0 transition-colors min-w-0 ${
                    status === "pending" ? "bg-warning-100 text-warning-700 hover:bg-warning-200" :
                    status === "in-progress" ? "bg-info-100 text-info-700 hover:bg-info-200" :
                    status === "completed" ? "bg-success-100 text-success-700 hover:bg-success-200" :
                    "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  <span className="truncate">
                    {status === "in-progress" ? "Start" :
                     status === "awaiting-parts" ? "Need Parts" :
                     status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
